/**
 * cameraAnimation — a tiny requestAnimationFrame tween for the imperative
 * `focusOn` camera fly-to. No animation dependency: the repo drives all motion
 * through r3f's render loop, so a hand-rolled lerp keeps the bundle lean.
 *
 * It lerps the camera position + the OrbitControls target (and, for an
 * orthographic camera, the frustum bounds) over `durationMs`. Each frame calls
 * `controls.update()`, which — exactly like `setCamera` / `zoomToFit` — emits
 * the OrbitControls `change` event that drei turns into an `invalidate()`, so
 * the tween renders correctly under `frameloop='demand'`.
 *
 * The clock / scheduler are injectable so the tween is unit-testable without a
 * real animation frame.
 */

import { Vector3 } from 'three';
import type { OrthographicCamera, PerspectiveCamera } from 'three';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';

export type EasingName = 'linear' | 'easeInOutCubic';

/** Smooth acceleration + deceleration. `f(0)=0`, `f(0.5)=0.5`, `f(1)=1`. */
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

export function resolveEasing(name: EasingName | undefined): (t: number) => number {
  return name === 'linear' ? (t) => t : easeInOutCubic;
}

export interface OrthoFrustum {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface AnimateCameraArgs {
  camera: PerspectiveCamera | OrthographicCamera;
  controls: OrbitControlsImpl;
  toPosition: Vector3;
  toTarget: Vector3;
  /** Target ortho frustum — only applied when `camera` is orthographic. */
  toFrustum?: OrthoFrustum;
  /** Duration in ms. `0` applies the end state immediately (no animation). */
  durationMs: number;
  easing?: (t: number) => number;
  /** Injectable clock (default `performance.now`). */
  now?: () => number;
  /** Injectable scheduler (default `requestAnimationFrame`). */
  raf?: (cb: (t: number) => void) => number;
  /** Injectable canceller (default `cancelAnimationFrame`). */
  caf?: (handle: number) => void;
}

/**
 * Animate the camera to `toPosition` / `toTarget` (+ optional ortho frustum).
 * Returns a cancel function that stops the tween in place.
 */
export function animateCamera(args: AnimateCameraArgs): () => void {
  const { camera, controls, toPosition, toTarget, toFrustum } = args;
  const duration = Math.max(0, args.durationMs);
  const ease = args.easing ?? easeInOutCubic;
  const now = args.now ?? (() => performance.now());
  const raf =
    args.raf ?? ((cb: (t: number) => void) => requestAnimationFrame(cb));
  const caf = args.caf ?? ((h: number) => cancelAnimationFrame(h));

  const fromPos = camera.position.clone();
  const fromTarget = controls.target.clone();
  const oc = camera as OrthographicCamera;
  const isOrtho = !!toFrustum && oc.isOrthographicCamera === true;
  const fromFrustum = isOrtho
    ? { left: oc.left, right: oc.right, top: oc.top, bottom: oc.bottom }
    : null;

  const apply = (t: number) => {
    camera.position.lerpVectors(fromPos, toPosition, t);
    controls.target.lerpVectors(fromTarget, toTarget, t);
    if (isOrtho && fromFrustum && toFrustum) {
      oc.left = fromFrustum.left + (toFrustum.left - fromFrustum.left) * t;
      oc.right = fromFrustum.right + (toFrustum.right - fromFrustum.right) * t;
      oc.top = fromFrustum.top + (toFrustum.top - fromFrustum.top) * t;
      oc.bottom =
        fromFrustum.bottom + (toFrustum.bottom - fromFrustum.bottom) * t;
      oc.updateProjectionMatrix();
    }
    controls.update();
  };

  if (duration === 0) {
    apply(1);
    return () => {};
  }

  let handle = 0;
  let cancelled = false;
  const start = now();
  const tick = () => {
    if (cancelled) return;
    const raw = Math.min(1, (now() - start) / duration);
    apply(ease(raw));
    if (raw < 1) handle = raf(tick);
  };
  handle = raf(tick);
  return () => {
    cancelled = true;
    caf(handle);
  };
}
